import { useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import AuthService from '@services/AuthService';

const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 404]);

const isNonRetryableStatus = (status) => NON_RETRYABLE_STATUS_CODES.has(status);

const appendNotification = (newNotification) => (notifications) => {
  const alreadyExists = notifications.some(
    (notification) =>
      notification.id_notificacion === newNotification.id_notificacion,
  );

  return alreadyExists ? notifications : [...notifications, newNotification];
};

const markNotificationAsRead = (idNotification) => (notifications) =>
  notifications.map((notification) =>
    notification.id_notificacion === idNotification
      ? { ...notification, leida: true }
      : notification,
  );

const getResponseErrorMessage = async (response) => {
  const defaultMessage = `Error HTTP ${response.status}`;

  try {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
      const body = await response.json();

      return body?.detail || body?.message || defaultMessage;
    }

    const text = await response.text();

    return text || defaultMessage;
  } catch (error) {
    console.error('No se pudo parsear el error response del SSE', error);

    return defaultMessage;
  }
};

const startNotificationsSSE = ({
  url,
  headers,
  retryTimeoutRef,
  retryCountRef,
  stoppedRef,
  setNotificaciones,
  setError,
  setIsConnected,
  setHasResolvedInitialNotifications,
}) => {
  const controller = new AbortController();

  stoppedRef.current = false;

  const isConnectionStopped = () =>
    stoppedRef.current || controller.signal.aborted;

  const clearRetryTimeout = () => {
    if (!retryTimeoutRef.current) return;

    clearTimeout(retryTimeoutRef.current);
    retryTimeoutRef.current = null;
  };

  const getRetryDelay = () =>
    Math.min(3000 * (retryCountRef.current + 1), 10000);

  const scheduleReconnect = () => {
    if (isConnectionStopped()) return;

    clearRetryTimeout();

    retryTimeoutRef.current = setTimeout(() => {
      retryCountRef.current += 1;
      connectSSE();
    }, getRetryDelay());
  };

  const handleOpen = async (response) => {
    if (response.ok) {
      retryCountRef.current = 0;
      setError(null);
      setIsConnected(true);
      setHasResolvedInitialNotifications(true);
      return;
    }

    setIsConnected(false);

    const errorMessage = await getResponseErrorMessage(response);
    const connectionError = new Error(errorMessage);

    setError(connectionError);

    if (isNonRetryableStatus(response.status)) {
      stoppedRef.current = true;
      controller.abort();
    }

    /*
     * Lanzamos el error para detener el sistema de reintentos interno
     * de fetchEventSource. La reconexión la administramos nosotros.
     */
    throw connectionError;
  };

  const handleMessage = (event) => {
    const rawData = event?.data?.trim();

    if (!rawData) return;

    try {
      const notification = JSON.parse(rawData);

      setNotificaciones(appendNotification(notification));
      setHasResolvedInitialNotifications(true);
    } catch (error) {
      console.error('SSE payload inválido:', rawData, error);
    }
  };

  const handleClose = () => {
    setIsConnected(false);
    scheduleReconnect();
  };

  const handleError = (error) => {
    setIsConnected(false);
    setError(error);

    console.error('Error en SSE:', error);

    /*
     * Al lanzarlo, fetchEventSource abandona su reintento automático.
     * Después, el catch de connectSSE programa nuestra reconexión.
     */
    throw error;
  };

  const connectSSE = async () => {
    if (isConnectionStopped()) return;

    try {
      await fetchEventSource(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
        openWhenHidden: true,
        onopen: handleOpen,
        onmessage: handleMessage,
        onclose: handleClose,
        onerror: handleError,
      });
    } catch (error) {
      if (isConnectionStopped()) return;

      setIsConnected(false);
      setError(error);

      console.error('Stream abortado o error:', error);

      scheduleReconnect();
    }
  };

  connectSSE();

  return () => {
    stoppedRef.current = true;

    controller.abort();
    clearRetryTimeout();

    setIsConnected(false);
    setHasResolvedInitialNotifications(false);
  };
};

export const useNotificationsSSE = ({ enabled = true }) => {
  const typeToken = AuthService.getTypeToken();
  const accessToken = AuthService.getAccessToken();

  const [notificaciones, setNotificaciones] = useState([]);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [hasResolvedInitialNotifications, setHasResolvedInitialNotifications] =
    useState(false);

  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !typeToken || !accessToken) return undefined;

    const url = `${process.env.NEXT_PUBLIC_API_URL}/notifications/`;

    const headers = {
      Authorization: `${typeToken} ${accessToken}`,
      Accept: 'text/event-stream',
    };

    return startNotificationsSSE({
      url,
      headers,
      retryTimeoutRef,
      retryCountRef,
      stoppedRef,
      setNotificaciones,
      setError,
      setIsConnected,
      setHasResolvedInitialNotifications,
    });
  }, [typeToken, accessToken, enabled]);

  const optimisticUpdateLeido = (idNotificacion) => {
    setNotificaciones(markNotificationAsRead(idNotificacion));
  };

  return {
    notificaciones,
    setNotificaciones,
    optimisticUpdateLeido,
    error,
    isConnected,
    hasResolvedInitialNotifications,
  };
};
